import { init, setUser, captureException } from '@sentry/browser';
import type { ErrorEvent, EventHint } from '@sentry/browser';

declare global {
  interface Window {
    // Published for lib/capture, so page bundles can report errors without
    // bundling and initialising a second copy of @sentry/browser
    __sentryCapture?: (error: unknown) => void;
  }
}

// URL patterns whose presence ANYWHERE in the stacktrace marks the event as
// noise. denyUrls only checks the top frame, so events whose top frame is
// first-party but whose origin is a browser extension still leak through.
const NOISE_FRAME_PATTERNS = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^webkit-masked-url:\/\//i,
  /collectedforms/i,
  /gadstat\.com/i,
  // HubSpot tracking loader. Sentry rewrites these to bpm:// after
  // source-map processing, so match the raw filenames seen in beforeSend
  /^\/pixels\.js$/i,
  /hs-scripts\.com/i,
  /hsappstatic\.net/i,
];

const NOISE_MESSAGE_PATTERNS = [
  /fixinatorInputs/,
  // @tailwindplus/elements: showModal on already-open dialog (Safari 16.4)
  /InvalidStateError.*invalid state/i,
  // Browser-API probing by password managers / autofill extensions
  /Not implemented on this platform/i,
  // Stripe.js injected by extensions without a publishableKey,
  // localised in some locales ("错误：API 参数无效")
  /Invalid API parameter\(s\)/i,
  /API 参数无效/,
  // Synthetic unhandledrejection events (isTrusted: false) dispatched by
  // extensions: no stack, no keys, nothing to debug
  /Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection/,
  /Object captured as promise rejection with keys: \[object has no keys\]/,
  // Clipboard permission denied by the embedding browser: environmental
  /Failed to execute 'writeText' on 'Clipboard'/,
  // Turnstile challenge (600xxx) and client (300xxx) failures are visitor-side;
  // config errors (1xxxxx) still report
  /\[Cloudflare Turnstile\] Error: [36]\d{5}/,
];

const isNoiseEvent = (event: ErrorEvent): boolean => {
  const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? [];
  if (frames.some(f => NOISE_FRAME_PATTERNS.some(p => p.test(f.filename ?? '') || p.test(f.abs_path ?? '')))) {
    return true;
  }

  const messages = event.exception?.values?.map(v => v.value ?? '') ?? [];
  if (event.message) messages.push(typeof event.message === 'string' ? event.message : '');
  return messages.some(m => NOISE_MESSAGE_PATTERNS.some(p => p.test(m)));
};

// Read config from meta tags set in the layout
const dsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content;
const userId = document.querySelector<HTMLMetaElement>('meta[name="sentry-user-id"]')?.content;
const environment = document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')?.content;
const release = document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content;

if (dsn) {
  init({
    dsn,
    environment: environment || undefined,
    release: release || undefined,
    beforeSend: (event: ErrorEvent, _hint: EventHint) => isNoiseEvent(event) ? null : event,
  });

  if (userId) {
    setUser({ id: userId });
  }

  window.__sentryCapture = captureException;
}

export { captureException };
